import React, { useState } from "react";
import { api, downloadPost } from "./api";
import { useProblems, FieldProblem, invalid } from "./form-check";
import { stamp } from "./Pass";
import { hhmmOf, STEP_LABEL, type VisitDetail } from "./Visits";
import { appNow } from "./clock";
import { easternToUtcMs } from "../server/station/windows";

/**
 * Escalation (build plan §10.1 #3; D2 #30, #11), as four collapsed sections
 * of the visit page, each with a one-line summary (M4 Stage 2): the referral steps —
 * campus first responders, 911 (then campus security), local EMS, the remote
 * provider, urgent care, the ER — each with its time, the contact from
 * Config ("number not set" when blank) and the outcome; emergency transport
 * (where, who went, return time); when the Director was notified (required
 * before a visit with 911 or a transport closes); the parent contact on the
 * phone tier; and the ER packet.
 */

const nowHHMM = () => hhmmOf(new Date(appNow()).toISOString());

export function VisitEscalation({ d, onSaved }: { d: VisitDetail; onSaved: (d: VisitDetail) => void }): React.ReactElement | null {
  const v = d.visit;
  // A voided visit is read-only, like an earlier version.
  const latest = d.latestVisitKey === v.visitKey && !d.voided;
  const [step, setStep] = useState("");
  const [time, setTime] = useState(nowHHMM());
  const [contact, setContact] = useState("");
  const [outcome, setOutcome] = useState("");
  const [reason, setReason] = useState("");
  const [transport, setTransport] = useState({ to: v.transportedTo, by: v.accompaniedBy, back: hhmmOf(v.returnedAtUtc) });
  const [director, setDirector] = useState(hhmmOf(v.directorNotifiedUtc));
  const [call, setCall] = useState({ number: "", toName: "", time: nowHHMM(), outcome: "", summary: "" });
  const [dismissal, setDismissal] = useState({ open: false, releasedBy: "", releasedTo: "", time: nowHHMM(), director: hhmmOf(v.directorNotifiedUtc), reason: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which section the error belongs to (it shows there). */
  const [errorAt, setErrorAt] = useState("");
  /** "Add a step" opens the step form. */
  const [stepOpen, setStepOpen] = useState(false);
  /** The server said this change rewrites what is recorded (its words): the Why box shows whatever the form thought. */
  const [askWhy, setAskWhy] = useState(false);
  // What is recorded here happens now: today's date on the logic clock (the test clock in test), not the visit's day.
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date(appNow()));
  const at = (hhmm: string) => (hhmm ? new Date(easternToUtcMs(day, hhmm)).toISOString() : "");
  const hasSteps = v.referralSteps.length > 0;
  /** Adding a step to recorded steps versions the visit (ReferralStepsJson is write-once): the Why box, every time. */
  const stepNeedsReason = hasSteps || askWhy;
  const stepRules = useProblems([
    ["esc-step", !step, "Choose the step."],
    ["esc-out", !outcome.trim(), "Write the outcome."],
    ["esc-reason", stepNeedsReason && !reason.trim(), "Write why the recorded visit is being changed."],
  ]);
  const dismissRules = useProblems([
    ["dm-by", !dismissal.releasedBy.trim(), "Write who released the student."],
    ["dm-to", !dismissal.releasedTo.trim(), "Write who the student was released to (name and relationship)."],
    ["dm-dir", !dismissal.director, "Record when the Director was notified: a medical dismissal notifies the Director."],
    ["dm-reason", Boolean(v.disposition) && v.disposition !== "Dismissed" && !dismissal.reason.trim(), "Write why the recorded visit is being changed."],
  ]);
  const callRules = useProblems([
    ["pc-num", call.number.replace(/\D/g, "").length < 7, "Enter the number dialled."],
    ["pc-out", !call.outcome, "Choose the outcome: reached, no answer, or left a message."],
    ["pc-sum", call.outcome === "Reached" && !call.summary.trim(), "Write a summary of what was said."],
  ]);
  if (!d.options.canEdit && !d.canErPacket) return null;
  const phoneTier = d.notifications.find((n) => n.method === "Phone" && n.tier !== "CallMeFirst");

  async function put(body: Record<string, unknown>, where: string): Promise<boolean> {
    setBusy(true);
    setError(null);
    setErrorAt(where);
    const r = await api<{ detail: VisitDetail }>("PUT", `/api/visits/${encodeURIComponent(v.visitKey)}`, body);
    setBusy(false);
    if (!r.ok) {
      if (r.data.code === "correctionReasonRequired") setAskWhy(true);
      setError(r.data.error ?? "Not saved.");
      return false;
    }
    onSaved(r.data.detail);
    return true;
  }
  async function addStep() {
    if (!stepRules.check()) return;
    const known = d.escalation.find((x) => x.step === step);
    const ok = await put({ referralSteps: [...v.referralSteps.map((x) => ({ ...x, at: x.atUtc })), { step, at: at(time), contact: contact || known?.contact || "", outcome }], ...(stepNeedsReason ? { correctionReason: reason } : {}) }, "steps");
    if (!ok) return;
    setStepOpen(false);
    // A blank form again, with no messages on it.
    setStep("");
    setContact("");
    setOutcome("");
    setReason("");
    setTime(nowHHMM());
    setAskWhy(false);
    stepRules.reset();
  }
  async function logCall() {
    if (!callRules.check()) return;
    setBusy(true);
    setError(null);
    setErrorAt("call");
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/parent-contact`, { number: call.number, toName: call.toName, at: at(call.time), outcome: call.outcome, summary: call.summary });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not logged.");
    setCall({ number: "", toName: "", time: nowHHMM(), outcome: "", summary: "" });
    callRules.reset();
    onSaved(r.data.detail);
  }
  async function dismiss() {
    if (!dismissRules.check()) return;
    setBusy(true);
    setError(null);
    setErrorAt("dismissal");
    const r = await api<{ detail: VisitDetail }>("POST", `/api/visits/${encodeURIComponent(v.visitKey)}/dismiss`, { releasedBy: dismissal.releasedBy, releasedTo: dismissal.releasedTo, at: at(dismissal.time), directorNotifiedAt: at(dismissal.director), correctionReason: dismissal.reason });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not recorded.");
    onSaved(r.data.detail);
  }
  async function packet() {
    setError(null);
    setErrorAt("steps");
    const e = await downloadPost(`/api/visits/${encodeURIComponent(v.visitKey)}/er-packet`, `${d.person.personKey}-er-packet.pdf`);
    if (e) setError(e);
  }

  const lastStep = v.referralSteps[v.referralSteps.length - 1];
  const directorSummary = v.directorNotifiedUtc ? `Notified ${stamp(v.directorNotifiedUtc)}` : d.directorRequired ? "Required before the visit closes (911 or a transport)" : "Not recorded";
  const contactSummary = !phoneTier ? "No phone call needed for this visit" : phoneTier.status === "Reached" ? "Parent reached" : phoneTier.status === "Withdrawn" ? "Withdrawn (visit voided)" : "Notification incomplete until a parent is reached";
  const err = (where: string) => (error && errorAt === where ? <p className="field-error" role="alert">{error}</p> : null);

  return (
    <>
      <Section
        title="Escalation and transport"
        summary={`${v.referralSteps.length ? `${v.referralSteps.length} step${v.referralSteps.length === 1 ? "" : "s"} · last: ${STEP_LABEL[lastStep.step] ?? lastStep.step} ${stamp(lastStep.atUtc)}` : "No steps"}${v.transportedTo ? ` · taken to ${v.transportedTo}` : ""}`}
        accent="border-l-red-600"
      >
        {d.canErPacket && <div><button type="button" className="btn-secondary min-h-11" onClick={() => void packet()}>ER packet (PDF)</button></div>}
        {hasSteps ? (
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {v.referralSteps.map((x, i) => <li key={i}><span className="font-medium">{STEP_LABEL[x.step] ?? x.step}</span> · {stamp(x.atUtc)} · {x.contact || "—"} · {x.outcome}</li>)}
          </ol>
        ) : (
          <p className="text-sm text-slate-600">No escalation steps.</p>
        )}
        {latest && d.options.canEdit && !stepOpen && (
          <button type="button" className="btn-secondary min-h-11" onClick={() => setStepOpen(true)}>Add a step</button>
        )}
        {latest && d.options.canEdit && stepOpen && (
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-sm font-semibold">New step</p>
            <div className="mt-1 grid gap-2 sm:grid-cols-2">
              <div>
                <label htmlFor="esc-step" className="field-label">Step</label>
                <select id="esc-step" className="field-input min-h-11" {...invalid("esc-step", stepRules.problems)} value={step} onChange={(e) => { setStep(e.target.value); setContact(d.escalation.find((x) => x.step === e.target.value)?.contact ?? ""); }}>
                  <option value="">Choose…</option>
                  {d.escalation.map((x) => <option key={x.step} value={x.step}>{x.label}</option>)}
                </select>
                <FieldProblem id="esc-step" problems={stepRules.problems} />
                {step && !d.escalation.find((x) => x.step === step)?.set && <p className="text-xs font-medium text-amber-900">Number not set (Config: Contacts).</p>}
              </div>
              <div><label htmlFor="esc-t" className="field-label">Time (today)</label><input id="esc-t" type="time" className="field-input" value={time} onChange={(e) => setTime(e.target.value)} /></div>
              <div><label htmlFor="esc-c" className="field-label">Who was contacted</label><input id="esc-c" className="field-input" value={contact} onChange={(e) => setContact(e.target.value)} /></div>
              <div><label htmlFor="esc-out" className="field-label">Outcome</label><input id="esc-out" className="field-input" {...invalid("esc-out", stepRules.problems)} value={outcome} onChange={(e) => setOutcome(e.target.value)} /><FieldProblem id="esc-out" problems={stepRules.problems} /></div>
            </div>
            {stepNeedsReason && (
              <div className="mt-2">
                <label htmlFor="esc-reason" className="field-label">The steps are recorded: a new step saves a new version. Why? <span className="text-red-600">*</span></label>
                <input id="esc-reason" className="field-input" placeholder="e.g. called 911 after the responders arrived" {...invalid("esc-reason", stepRules.problems)} value={reason} onChange={(e) => setReason(e.target.value)} />
                <FieldProblem id="esc-reason" problems={stepRules.problems} />
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void addStep()}>Add the step</button>
              <button type="button" className="btn-secondary min-h-11" onClick={() => { setStepOpen(false); stepRules.reset(); }}>Cancel</button>
            </div>
          </div>
        )}
        {latest && d.options.canEdit && (
          <div className="rounded-md border border-slate-200 p-3">
            <p className="text-sm font-semibold">Emergency transport</p>
            <div className="mt-1 grid gap-2 sm:grid-cols-3">
              <div><label htmlFor="tr-to" className="field-label">Taken to</label><input id="tr-to" className="field-input" value={transport.to} onChange={(e) => setTransport({ ...transport, to: e.target.value })} /></div>
              <div><label htmlFor="tr-by" className="field-label">Accompanied by</label><input id="tr-by" className="field-input" value={transport.by} onChange={(e) => setTransport({ ...transport, by: e.target.value })} /></div>
              <div><label htmlFor="tr-back" className="field-label">Returned at</label><input id="tr-back" type="time" className="field-input" value={transport.back} onChange={(e) => setTransport({ ...transport, back: e.target.value })} /></div>
            </div>
            <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void put({ transportedTo: transport.to, accompaniedBy: transport.by, returnedAt: at(transport.back) }, "transport")}>Save the transport</button>
            <p className="mt-1 text-xs text-slate-500">A recorded destination or companion changes only as a new version (Save on the visit form, with a reason).</p>
          </div>
        )}
        {err("steps")}
        {err("transport")}
      </Section>

      <Section title="Director notified" summary={directorSummary} warn={d.directorRequired && !v.directorNotifiedUtc}>
        {latest && d.options.canEdit ? (
          <div className="flex flex-wrap items-end gap-2">
            <div><label htmlFor="dir-t" className="field-label">At (today){d.directorRequired && <span className="text-red-600"> *</span>}</label><input id="dir-t" type="time" className="field-input" value={director} onChange={(e) => setDirector(e.target.value)} /></div>
            <button type="button" className="btn-secondary min-h-11" disabled={busy || !director} onClick={() => void put({ directorNotifiedAt: at(director) }, "director")}>Save</button>
            {v.directorNotifiedUtc && <span className="text-sm text-emerald-800">Notified {stamp(v.directorNotifiedUtc)}</span>}
          </div>
        ) : (
          <p className="text-sm">{directorSummary}</p>
        )}
        {err("director")}
      </Section>

      {phoneTier && (
        <Section title="Parent contact" summary={contactSummary} warn={phoneTier.status !== "Reached" && phoneTier.status !== "Withdrawn"}>
          {latest && d.options.canEdit ? (
            <>
              <div className="grid gap-2 sm:grid-cols-4">
                <div><label htmlFor="pc-num" className="field-label">Number dialled</label><input id="pc-num" type="tel" className="field-input" {...invalid("pc-num", callRules.problems)} value={call.number} onChange={(e) => setCall({ ...call, number: e.target.value })} /><FieldProblem id="pc-num" problems={callRules.problems} /></div>
                <div><label htmlFor="pc-to" className="field-label">Spoke to (or tried)</label><input id="pc-to" className="field-input" value={call.toName} onChange={(e) => setCall({ ...call, toName: e.target.value })} /></div>
                <div><label htmlFor="pc-t" className="field-label">Time (today)</label><input id="pc-t" type="time" className="field-input" value={call.time} onChange={(e) => setCall({ ...call, time: e.target.value })} /></div>
                <div>
                  <label htmlFor="pc-out" className="field-label">Outcome</label>
                  <select id="pc-out" className="field-input" {...invalid("pc-out", callRules.problems)} value={call.outcome} onChange={(e) => setCall({ ...call, outcome: e.target.value })}>
                    <option value="">Choose…</option>
                    <option value="Reached">Reached</option>
                    <option value="NoAnswer">No answer</option>
                    <option value="LeftMessage">Left a message</option>
                  </select>
                  <FieldProblem id="pc-out" problems={callRules.problems} />
                </div>
              </div>
              {call.outcome === "Reached" && (
                <div><label htmlFor="pc-sum" className="field-label">Summary of the conversation<span className="text-red-600"> *</span></label><textarea id="pc-sum" className="field-input min-h-16" {...invalid("pc-sum", callRules.problems)} value={call.summary} onChange={(e) => setCall({ ...call, summary: e.target.value })} /><FieldProblem id="pc-sum" problems={callRules.problems} /></div>
              )}
              <button type="button" className="btn-secondary min-h-11" disabled={busy} onClick={() => void logCall()}>Log the call</button>
            </>
          ) : (
            <p className="text-sm">{contactSummary}</p>
          )}
          {err("call")}
        </Section>
      )}

      {d.person.personType === "Student" && (
        <Section title="Medical dismissal" summary={d.person.departed ? "Left the session" : "Not dismissed"} warn={d.person.departed} accent="border-l-red-300">
          {d.person.departed ? (
            <p className="text-sm font-semibold text-red-800">This student has left the session (medical dismissal or departure). Their schedule has stopped; medications are to be returned.</p>
          ) : !latest || !d.options.canEdit ? (
            <p className="text-sm text-slate-600">Not dismissed.</p>
          ) : !dismissal.open ? (
            <button type="button" className="btn-secondary min-h-11" onClick={() => setDismissal({ ...dismissal, open: true })}>Medical dismissal…</button>
          ) : (
            <div>
              <p className="text-xs text-slate-600">The student leaves the session: the medication schedule stops (later doses are departed, not missed) and their medications are to be returned. The Director must be notified.</p>
              <div className="mt-1 grid gap-2 sm:grid-cols-2">
                <div><label htmlFor="dm-by" className="field-label">Released by</label><input id="dm-by" className="field-input" {...invalid("dm-by", dismissRules.problems)} value={dismissal.releasedBy} onChange={(e) => setDismissal({ ...dismissal, releasedBy: e.target.value })} /><FieldProblem id="dm-by" problems={dismissRules.problems} /></div>
                <div><label htmlFor="dm-to" className="field-label">Released to (name, relationship)</label><input id="dm-to" className="field-input" {...invalid("dm-to", dismissRules.problems)} value={dismissal.releasedTo} onChange={(e) => setDismissal({ ...dismissal, releasedTo: e.target.value })} /><FieldProblem id="dm-to" problems={dismissRules.problems} /></div>
                <div><label htmlFor="dm-t" className="field-label">Time of release (today)</label><input id="dm-t" type="time" className="field-input" value={dismissal.time} onChange={(e) => setDismissal({ ...dismissal, time: e.target.value })} /></div>
                <div><label htmlFor="dm-dir" className="field-label">Director notified at</label><input id="dm-dir" type="time" className="field-input" {...invalid("dm-dir", dismissRules.problems)} value={dismissal.director} onChange={(e) => setDismissal({ ...dismissal, director: e.target.value })} /><FieldProblem id="dm-dir" problems={dismissRules.problems} /></div>
              </div>
              {v.disposition && v.disposition !== "Dismissed" && (
                <div className="mt-2"><label htmlFor="dm-reason" className="field-label">The disposition is recorded: this saves a new version. Why? <span className="text-red-600">*</span></label><input id="dm-reason" className="field-input" {...invalid("dm-reason", dismissRules.problems)} value={dismissal.reason} onChange={(e) => setDismissal({ ...dismissal, reason: e.target.value })} /><FieldProblem id="dm-reason" problems={dismissRules.problems} /></div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void dismiss()}>Record the medical dismissal</button>
                <button type="button" className="btn-secondary min-h-11" onClick={() => setDismissal({ ...dismissal, open: false })}>Cancel</button>
              </div>
            </div>
          )}
          {err("dismissal")}
        </Section>
      )}
    </>
  );
}

/**
 * A collapsed section of the visit page with a one-line summary (owner's
 * walk-through, 2026-10-18: one tall form). Closed by default; the summary
 * says what is in it, in amber when it needs doing.
 */
export function Section({ title, summary, warn = false, accent = "", defaultOpen = false, children }: { title: string; summary: string; warn?: boolean; accent?: string; defaultOpen?: boolean; children: React.ReactNode }) {
  return (
    <details className={`section-card ${accent ? `border-l-4 ${accent}` : ""}`} open={defaultOpen || undefined}>
      <summary className="flex min-h-11 cursor-pointer flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-program focus-visible:ring-offset-2">
        <h3 className="text-base font-semibold">{title}</h3>
        <span className={`text-sm ${warn ? "font-medium text-amber-900" : "text-slate-600"}`}>{summary}</span>
      </summary>
      <div className="mt-3 space-y-3">{children}</div>
    </details>
  );
}
